<script setup>
import { onMounted, ref, computed } from 'vue';
import DSButton from 'primevue/button';
import SplitModal from '@/components/modals/SplitModal.vue';
import Steps from 'primevue/steps';
import purpleCoinsImage from '@/assets/images/purple-coins-zoomed.png';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import RollingFundingExplanation from './RollingFundingExplanation.vue';
import RollingVersusFixedComparison from './RollingVersusFixedComparison.vue';
import FundingNeedsForm from '@/components/forms/FundingNeedsForm.vue';
import { useRollingFundingInquiries } from '@/composables/useRollingFundingInquiries';

const props = defineProps({
  visible: Boolean,
});

const activeIndex = ref(0);
const emit = defineEmits(['close']);
const fundingFormRef = ref(null);

const { latestValidRollingFundingInquiry } = useRollingFundingInquiries();

const items = ref([
  {
    label: 'Compare Funding Options',
    buttonLabel: 'Compare Funding Options',
  },
  {
    label: 'Find Best Option', // temp label
    buttonLabel: 'Continue to Request',
  },
  {
    label: 'Connect with Sales', // temp label
    buttonLabel: latestValidRollingFundingInquiry.value
      ? 'Resubmit Request'
      : 'Submit Request',
  },
]);

const handleWizardClose = () => {
  analytics.track(SEGMENT_EVENTS.if_rolling_funding_comparison_wizard_closed, {
    step_name: items.value[activeIndex.value]?.label || 'Unknown',
  });
  emit('close');
  activeIndex.value = 0;
};

onMounted(() => {
  analytics.track(SEGMENT_EVENTS.if_rolling_funding_comparison_wizard_opened);
});

const handleButtonAction = async () => {
  if (activeIndex.value !== items.value.length - 1) {
    if (activeIndex.value === 0) {
      analytics.track(
        SEGMENT_EVENTS.if_rolling_funding_clicked_compare_options,
      );
    } else {
      analytics.track(
        SEGMENT_EVENTS.if_rolling_funding_clicked_continue_to_request,
      );
    }
    activeIndex.value++;
  } else {
    await fundingFormRef.value.submit();
  }
};

const handleBackAction = () => {
  if (activeIndex.value > 0) {
    activeIndex.value--;
  }
};

const isSubmitDisabled = computed(() => {
  // Only apply disabled logic on the form step (step 2)
  if (activeIndex.value !== 2) {
    return false;
  }

  // If form ref is not available yet, disable the button
  if (!fundingFormRef.value) {
    return true;
  }

  const { isFormValid } = fundingFormRef.value ?? {};
  const resubmittingWithoutChanges =
    latestValidRollingFundingInquiry.value &&
    !fundingFormRef.value.hasFormChanged;

  return !isFormValid || resubmittingWithoutChanges;
});

const handleSubmitSuccess = () => {
  handleWizardClose();
};
</script>

<template>
  <SplitModal
    :visible="props.visible"
    title="Find Your Best Funding Option"
    :backEnabled="activeIndex > 0"
    @back="handleBackAction"
    @hide="handleWizardClose"
  >
    <template #content v-if="activeIndex === 0">
      <RollingFundingExplanation />
    </template>

    <template #content v-else-if="activeIndex === 1">
      <RollingVersusFixedComparison />
    </template>

    <template #content v-else-if="activeIndex === 2">
      <FundingNeedsForm
        ref="fundingFormRef"
        @submit-success="handleSubmitSuccess"
      />
    </template>

    <template #footer>
      <div :class="$style.controls">
        <div :class="$style.stepsContainer">
          <Steps :model="items" :readonly="false" :class="$style.steps">
            <template #item="{ item }">
              <div
                :class="[
                  $style.step__indicators,
                  {
                    [$style.step__indicators__active]:
                      items.indexOf(item) <= activeIndex,
                  },
                ]"
                @click="activeIndex = items.indexOf(item)"
              ></div>
            </template>
          </Steps>
        </div>
        <DSButton
          :class="$style.controls__item"
          :label="items[activeIndex].buttonLabel"
          :disabled="isSubmitDisabled"
          @click="handleButtonAction"
        />
      </div>
    </template>

    <template #image>
      <img :src="purpleCoinsImage" alt="Purple coins" />
    </template>
  </SplitModal>
</template>

<style lang="less" module>
.controls {
  display: flex;
  justify-content: space-between;
  column-gap: 24px;
  flex-direction: column;
}

.controls__item {
  width: 100%;
  margin: 0;
}

.step__indicators {
  cursor: pointer;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #d1d5db;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #9ca3af;
  }
}

.step__indicators__active {
  background-color: #7f30ff !important;
}

.stepsContainer {
  justify-content: center;
  display: flex;
  padding: 0 24px 12px 24px;
}

.steps {
  margin: 0 auto;
  gap: 16px !important;

  :global(.p-steps-item) {
    flex: 0 0 auto !important;
    margin: 0 8px !important;
  }

  :global(.p-steps-item::before) {
    border-top: none !important;
  }
}

@media (min-width: 900px) {
  .content {
    max-width: 600px;
  }
}
</style>
